import React, { useEffect, useRef } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import { useStore } from './store';
import { useShallow } from 'zustand/react/shallow';
import { ToastProvider } from './components/ToastProvider';
import SentryErrorBoundary from './components/SentryErrorBoundary';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import SignedOutPage from './pages/SignedOutPage';
import Dashboard from './pages/Dashboard';
import Patients from './pages/PatientList';
import PatientDetail from './pages/PatientDetail';
import DocumentViewer from './pages/DocumentViewer';
import VisitDetail from './pages/VisitDetail';
import Documents from './pages/Documents';
import Tasks from './pages/Tasks';
import IntakeManager from './pages/IntakeManager';
import Intake3 from './pages/Intake3';
import Billing from './pages/Billing';
import Orders from './pages/Orders';
import AuthManager from './pages/AuthManager';
import Reporting from './pages/Reporting';
import Admin from './pages/Admin';
import InternalAdmin from './pages/InternalAdmin';
import Testing from './pages/Testing';
import TestingX12_270 from './pages/TestingX12_270';
import TestingX12_837 from './pages/TestingX12_837';
import TestingX12_835 from './pages/TestingX12_835';
import TestingStedi_270 from './pages/TestingStedi_270';
import SentryTest from './components/SentryTest';
import TestingStedi_COB from './pages/TestingStedi_COB';
import TestingStedi_837I from './pages/TestingStedi_837I';
import ClinicianManager from './pages/ClinicianManager';
import RogerDocOverview from './pages/RogerDocOverview';
import RogerDocUsers from './pages/RogerDocUsers';
import RogerDocUsage from './pages/RogerDocUsage';
import QALegacy from './pages/QALegacy';
import QA from './pages/QA';
import CodingSearch from './pages/CodingSearch';
import CodingDiagnosis from './pages/CodingDiagnosis';
import WorksheetGoals from './pages/WorksheetGoals';
import Resources from './pages/Resources';
import Customizations from './pages/Customizations';
import Layout from './components/Layout';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, authChecked, checkAuthStatus } = useStore(
    useShallow(state => ({
      isAuthenticated: state.isAuthenticated,
      authChecked: state.authChecked,
      checkAuthStatus: state.checkAuthStatus,
    }))
  );
  const authCheckedRef = useRef(false);

  useEffect(() => {
    // Only check authentication status once on app load
    if (!authCheckedRef.current) {
      authCheckedRef.current = true;
      checkAuthStatus();
    }
  }, [checkAuthStatus]);

  // Show loading while checking auth status
  if (!authChecked) {
    return (
      <div className='min-h-screen bg-background flex items-center justify-center'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to='/login' replace />;
  }

  return <>{children}</>;
};

// App Component
const App: React.FC = () => {
  return (
    <SentryErrorBoundary>
      <ToastProvider>
        <Router>
          <NuqsAdapter>
            <Routes>
              <Route path='/login' element={<LoginPage />} />
              <Route path='/auth/callback' element={<AuthCallback />} />
              <Route path='/signed-out' element={<SignedOutPage />} />
              <Route
                path='/'
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to='/dashboard' replace />} />
                <Route path='dashboard' element={<Dashboard />} />
                <Route path='patients' element={<Patients />} />
                <Route path='patients/:id' element={<PatientDetail />} />
                <Route
                  path='patients/:patientId/documents/:documentId'
                  element={<DocumentViewer />}
                />
                <Route
                  path='patients/:patientId/visits/:visitId'
                  element={<VisitDetail />}
                />
                <Route path='documents' element={<Documents />} />
                <Route path='tasks' element={<Tasks />} />
                <Route path='intake/:intakeId' element={<Intake3 />} />
                <Route path='intake-manager' element={<IntakeManager />} />
                <Route path='billing' element={<Billing />} />
                <Route path='orders' element={<Orders />} />
                <Route path='auth-manager' element={<AuthManager />} />
                <Route path='reporting' element={<Reporting />} />
                <Route path='admin' element={<Admin />} />
                <Route path='internal-admin' element={<InternalAdmin />} />
                <Route
                  path='clinician-manager'
                  element={<ClinicianManager />}
                />
                <Route
                  path='rogerdoc/overview'
                  element={<RogerDocOverview />}
                />
                <Route path='rogerdoc/users' element={<RogerDocUsers />} />
                <Route path='rogerdoc/usage' element={<RogerDocUsage />} />
                <Route path='testing' element={<Testing />} />
                <Route path='testing/x12-270' element={<TestingX12_270 />} />
                <Route path='testing/x12-837' element={<TestingX12_837 />} />
                <Route path='testing/x12-835' element={<TestingX12_835 />} />
                <Route
                  path='testing/stedi-270'
                  element={<TestingStedi_270 />}
                />
                <Route path='testing/sentry' element={<SentryTest />} />
                <Route
                  path='testing/stedi-cob'
                  element={<TestingStedi_COB />}
                />
                <Route
                  path='testing/stedi-837i'
                  element={<TestingStedi_837I />}
                />
                <Route path='qa-legacy' element={<QALegacy />} />
                <Route path='qa-legacy/:visitId' element={<QALegacy />} />
                <Route path='qa' element={<QA />} />
                <Route path='qa/:visitId' element={<QA />} />
                <Route path='coding' element={<CodingSearch />} />
                <Route
                  path='coding/:diagnosisId'
                  element={<CodingDiagnosis />}
                />
                <Route
                  path='coding/:diagnosisId/states/:stateId'
                  element={<CodingDiagnosis />}
                />
                <Route path='worksheet-goals' element={<WorksheetGoals />} />
                <Route path='customizations' element={<Customizations />} />
                <Route path='resources' element={<Resources />} />
              </Route>
            </Routes>
          </NuqsAdapter>
        </Router>
      </ToastProvider>
    </SentryErrorBoundary>
  );
};

export default App;
